const SPEEDS = [0.25, 0.5, 1, 1.5, 2, 3];

export default class ReplayControls {
    constructor({ onClose = null } = {}) {
        this.onClose = onClose;
        this.onPrev = null;
        this.onPlayPause = null;
        this.onNext = null;
        this.onSpeedChange = null;
        this.onFilterChange = null;

        this.isPlaying = false;
        this.eventTypes = [];
        this.selectedTypes = new Set();

        this.customCSS = `
            .replay-overlay {
                position: fixed;
                top: 80px;
                left: 50%;
                transform: translateX(-50%);
                z-index: 9999;
                display: none;
                flex-direction: column;
                width: 320px;
                background: #fff;
                border-radius: 14px;
                box-shadow: 0 16px 48px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04);
                overflow: hidden;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                user-select: none;
            }
            .replay-overlay.visible {
                display: flex;
            }
            .replay-overlay [hidden] {
                display: none !important;
            }

            /* Header */
            .replay-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 12px 16px;
                background: linear-gradient(135deg, #855cd6 0%, #6b4bb5 100%);
                color: #fff;
                font-size: 0.85rem;
                font-weight: 700;
                cursor: grab;
                letter-spacing: 0.3px;
            }
            .replay-header:active {
                cursor: grabbing;
            }
            .replay-title {
                display: flex;
                align-items: center;
                gap: 8px;
            }
            .replay-close {
                background: none;
                border: none;
                color: #fff;
                font-size: 1.1rem;
                cursor: pointer;
                width: 26px;
                height: 26px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 6px;
                transition: background 0.2s;
                padding: 0;
            }
            .replay-close:hover {
                background: rgba(255,255,255,0.2);
            }

            /* Step Info Panel */
            .replay-step-info {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 10px;
                padding: 16px 20px 12px;
                background: linear-gradient(180deg, #faf8ff 0%, #ffffff 100%);
                border-bottom: 1px solid #f0ebfa;
            }
            .replay-timestamp {
                font-family: "SF Mono", Monaco, "Cascadia Code", monospace;
                font-size: 0.8rem;
                color: #9b7ee0;
                font-weight: 600;
                letter-spacing: 0.5px;
                background: #f3eefc;
                padding: 4px 12px;
                border-radius: 20px;
                border: 1px solid #e5ddf5;
            }
            .replay-event-badge {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                padding: 6px 16px;
                border-radius: 8px;
                font-size: 0.85rem;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.8px;
                color: #fff;
                background: linear-gradient(135deg, #855cd6 0%, #9b7ee0 100%);
                box-shadow: 0 2px 8px rgba(133, 92, 214, 0.25);
            }
            .replay-event-badge::before {
                content: "";
                width: 6px;
                height: 6px;
                background: #c6f6d5;
                border-radius: 50%;
                box-shadow: 0 0 0 2px rgba(198, 246, 213, 0.4);
                animation: pulse-dot 2s ease-in-out infinite;
            }
            @keyframes pulse-dot {
                0%, 100% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.6; transform: scale(0.85); }
            }
            .replay-author {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 0.9rem;
                font-weight: 600;
                color: #4a3f6b;
            }
            .replay-author-avatar {
                width: 22px;
                height: 22px;
                border-radius: 50%;
                background: linear-gradient(135deg, #855cd6 0%, #ff8c1a 100%);
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                font-size: 0.65rem;
                font-weight: 800;
                flex-shrink: 0;
            }
            .replay-step-counter {
                font-size: 0.7rem;
                font-weight: 700;
                color: #a080d0;
                font-family: "SF Mono", Monaco, monospace;
            }

            /* Progress */
            .replay-progress {
                height: 4px;
                background: #f0ebfa;
                width: 100%;
                position: relative;
            }
            .replay-progress-bar {
                height: 100%;
                width: 0%;
                background: linear-gradient(90deg, #855cd6, #ff8c1a);
                transition: width 0.4s cubic-bezier(0.16, 1, 0.3, 1);
            }

            /* Controls */
            .replay-body {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 16px;
                padding: 16px 20px;
                background: #fff;
            }
            .replay-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 46px;
                height: 46px;
                border-radius: 50%;
                border: none;
                background: #fff;
                color: #855cd6;
                cursor: pointer;
                box-shadow: 0 2px 10px rgba(133, 92, 214, 0.12);
                transition: all 0.2s ease;
            }
            .replay-btn:hover {
                background: #855cd6;
                color: #fff;
                transform: translateY(-2px);
                box-shadow: 0 6px 16px rgba(133, 92, 214, 0.3);
            }
            .replay-btn:active {
                transform: translateY(0);
            }
            .replay-btn svg {
                width: 20px;
                height: 20px;
                fill: currentColor;
                stroke: none;
            }
            .replay-btn-play svg {
                width: 22px;
                height: 22px;
                margin-left: 2px;
            }

            /* Speed & filter */
            .replay-settings {
                display: flex;
                flex-direction: column;
                gap: 10px;
                padding: 12px 16px 14px;
                background: #faf8ff;
                border-top: 1px solid #f0ebfa;
            }
            .replay-settings-label {
                display: block;
                margin-bottom: 6px;
                font-size: 0.68rem;
                font-weight: 700;
                color: #7a6f99;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }
            .replay-speed-options {
                display: flex;
                gap: 2px;
                padding: 3px;
                background: #f0ebfa;
                border-radius: 8px;
            }
            .replay-speed-btn {
                flex: 1;
                padding: 5px 0;
                border: none;
                border-radius: 6px;
                background: none;
                color: #6b5b95;
                font: inherit;
                font-size: 0.72rem;
                font-weight: 700;
                cursor: pointer;
                transition: background 0.15s ease, color 0.15s ease;
            }
            .replay-speed-btn:hover {
                background: rgba(133, 92, 214, 0.12);
            }
            .replay-speed-btn.active {
                background: #855cd6;
                color: #fff;
                box-shadow: 0 1px 4px rgba(133, 92, 214, 0.35);
            }
            .replay-filter-toggle {
                display: flex;
                align-items: center;
                gap: 6px;
                width: 100%;
                padding: 7px 10px;
                border: 1px solid #e5ddf5;
                border-radius: 8px;
                background: #fff;
                color: #4a3f6b;
                font: inherit;
                font-size: 0.8rem;
                font-weight: 700;
                cursor: pointer;
                transition: border-color 0.15s ease;
            }
            .replay-filter-toggle:hover {
                border-color: #c9b8ef;
            }
            .replay-filter-summary {
                margin-left: auto;
                padding: 1px 8px;
                border-radius: 10px;
                background: #f3eefc;
                color: #9b7ee0;
                font-size: 0.72rem;
            }
            .replay-filter-arrow {
                font-size: 0.7rem;
                color: #9b7ee0;
                transition: transform 0.2s ease;
            }
            .replay-filter-toggle[aria-expanded="true"] .replay-filter-arrow {
                transform: rotate(180deg);
            }
            .replay-filter-panel {
                display: flex;
                flex-direction: column;
                gap: 6px;
            }
            .replay-filter-actions {
                display: flex;
                gap: 6px;
            }
            .replay-filter-actions button {
                padding: 3px 10px;
                border: none;
                border-radius: 6px;
                background: #f0ebfa;
                color: #6b4bb5;
                font: inherit;
                font-size: 0.72rem;
                font-weight: 700;
                cursor: pointer;
            }
            .replay-filter-actions button:hover {
                background: #e5ddf5;
            }
            .replay-filter-list {
                display: flex;
                flex-direction: column;
                max-height: 200px;
                overflow-y: auto;
                padding: 4px;
                background: #fff;
                border: 1px solid #f0ebfa;
                border-radius: 8px;
            }
            .replay-filter-option {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 5px 6px;
                border-radius: 6px;
                font-size: 0.8rem;
                color: #4a3f6b;
                cursor: pointer;
            }
            .replay-filter-option:hover {
                background: #faf8ff;
            }
            .replay-filter-option input {
                margin: 0;
                accent-color: #855cd6;
                cursor: pointer;
            }
            .replay-filter-name {
                flex: 1;
            }
            .replay-filter-count {
                font-family: "SF Mono", Monaco, monospace;
                font-size: 0.7rem;
                font-weight: 700;
                color: #a89bc7;
            }
            .replay-filter-empty {
                padding: 6px;
                font-size: 0.75rem;
                color: #a89bc7;
            }
        `;

        this.injectCSS();
        this.createElement();
        this.setupDrag();
    }

    injectCSS() {
        const style = document.createElement("style");
        style.innerHTML = this.customCSS;
        document.head.appendChild(style);
    }

    createElement() {
        this.el = document.createElement("div");
        this.el.className = "replay-overlay";
        this.el.innerHTML = `
             <div class="replay-header" id="replay-header">
                <div class="replay-title">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <polygon points="5 3 19 12 5 21 5 3"/>
                    </svg>
                    Reproducción
                </div>
                <button class="replay-close" id="replay-close" title="Cerrar">✕</button>
            </div>

            <div class="replay-step-info">
                <div class="replay-timestamp" id="replay-timestamp">--/--/-- --:--:--</div>
                <div class="replay-event-badge" id="replay-event">Esperando...</div>
                <div class="replay-author" id="replay-author">
                    <div class="replay-author-avatar" id="replay-author-avatar">?</div>
                    <span id="replay-author-name">--</span>
                </div>
                <div class="replay-step-counter" id="replay-step-counter">0 / 0</div>
            </div>

            <div class="replay-progress">
                <div class="replay-progress-bar" id="replay-progress-bar"></div>
            </div>

            <div class="replay-body">
                <button class="replay-btn" id="replay-prev" title="Paso anterior">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
                    </svg>
                </button>
                <button class="replay-btn replay-btn-play" id="replay-play" title="Reproducir / Pausar">
                    <svg viewBox="0 0 24 24" fill="currentColor" id="replay-play-icon">
                        <path d="M8 5v14l11-7z"/>
                    </svg>
                </button>
                <button class="replay-btn" id="replay-next" title="Paso siguiente">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
                    </svg>
                </button>
            </div>

            <div class="replay-settings">
                <div>
                    <span class="replay-settings-label">Velocidad</span>
                    <div class="replay-speed-options" id="replay-speed-options">
                        ${SPEEDS.map(
                            (rate) =>
                                `<button class="replay-speed-btn${rate === 1 ? " active" : ""}" data-rate="${rate}" title="Velocidad x${rate}">x${rate}</button>`,
                        ).join("")}
                    </div>
                </div>

                <button class="replay-filter-toggle" id="replay-filter-toggle" aria-expanded="false">
                    <span>Filtrar eventos a reproducir</span>
                    <span class="replay-filter-summary" id="replay-filter-summary">0/0</span>
                    <span class="replay-filter-arrow">▾</span>
                </button>
                <div class="replay-filter-panel" id="replay-filter-panel" hidden>
                    <div class="replay-filter-actions">
                        <button id="replay-filter-all">Mostrar todos</button>
                        <button id="replay-filter-none">No mostrar ninguno</button>
                    </div>
                    <div class="replay-filter-list" id="replay-filter-list"></div>
                </div>
            </div>
        `;

        document.body.appendChild(this.el);

        const query = (selector) => this.el.querySelector(selector);

        query("#replay-close").onclick = () => {
            this.hide();
            this.onClose?.();
        };
        query("#replay-prev").onclick = () => this.onPrev?.();
        query("#replay-next").onclick = () => this.onNext?.();
        query("#replay-play").onclick = () => this.togglePlayPause();

        query("#replay-speed-options").onclick = (e) => {
            const button = e.target.closest(".replay-speed-btn");
            if (button) this.setSpeed(Number(button.dataset.rate));
        };

        query("#replay-filter-toggle").onclick = () => {
            const panel = query("#replay-filter-panel");
            panel.hidden = !panel.hidden;
            query("#replay-filter-toggle").setAttribute(
                "aria-expanded",
                String(!panel.hidden),
            );
        };
        query("#replay-filter-all").onclick = () => {
            this.selectedTypes = new Set(this.eventTypes.map((t) => t.type));
            this._emitFilterChange();
        };
        query("#replay-filter-none").onclick = () => {
            this.selectedTypes.clear();
            this._emitFilterChange();
        };
        query("#replay-filter-list").onchange = (e) => {
            if (!e.target.matches('input[type="checkbox"]')) return;

            if (e.target.checked) this.selectedTypes.add(e.target.value);
            else this.selectedTypes.delete(e.target.value);
            this._emitFilterChange();
        };
    }

    setupDrag() {
        const header = this.el.querySelector("#replay-header");
        let isDragging = false;
        let startX, startY, initialLeft, initialTop;

        const onMouseDown = (e) => {
            isDragging = true;
            const rect = this.el.getBoundingClientRect();

            if (!this.el.style.left || this.el.style.left === "50%") {
                this.el.style.transform = "none";
                this.el.style.left = rect.left + "px";
                this.el.style.top = rect.top + "px";
            }

            startX = e.clientX || e.touches?.[0]?.clientX;
            startY = e.clientY || e.touches?.[0]?.clientY;
            initialLeft = parseFloat(this.el.style.left) || rect.left;
            initialTop = parseFloat(this.el.style.top) || rect.top;
            header.style.cursor = "grabbing";
        };

        const onMouseMove = (e) => {
            if (!isDragging) return;

            const clientX = e.clientX || e.touches?.[0]?.clientX;
            const clientY = e.clientY || e.touches?.[0]?.clientY;
            const dx = clientX - startX;
            const dy = clientY - startY;
            this.el.style.left = initialLeft + dx + "px";
            this.el.style.top = initialTop + dy + "px";
        };

        const onMouseUp = (e) => {
            isDragging = false;
            header.style.cursor = "grab";
        };

        header.addEventListener("mousedown", onMouseDown);
        header.addEventListener("touchstart", onMouseDown, { passive: false });
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("touchmove", onMouseMove, { passive: false });
        document.addEventListener("mouseup", onMouseUp);
        document.addEventListener("touchend", onMouseUp);
    }

    togglePlayPause() {
        this.isPlaying = !this.isPlaying;
        const icon = this.el.querySelector("#replay-play-icon");
        if (this.isPlaying) {
            icon.innerHTML = `<rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>`;
            icon.setAttribute("viewBox", "0 0 24 24");
        } else {
            icon.innerHTML = `<path d="M8 5v14l11-7z"/>`;
            icon.setAttribute("viewBox", "0 0 24 24");
        }
        this.onPlayPause?.(this.isPlaying);
    }

    setSpeed(rate) {
        this.el.querySelectorAll(".replay-speed-btn").forEach((button) => {
            button.classList.toggle(
                "active",
                Number(button.dataset.rate) === rate,
            );
        });
        this.onSpeedChange?.(rate);
    }

    setEventTypes(types) {
        this.eventTypes = types;
        this.selectedTypes = new Set(types.map((t) => t.type));

        const list = this.el.querySelector("#replay-filter-list");
        if (types.length === 0) {
            const empty = document.createElement("div");
            empty.className = "replay-filter-empty";
            empty.textContent = "No hay eventos registrados";
            list.replaceChildren(empty);
        } else {
            list.replaceChildren(
                ...types.map((t) => this._createFilterOption(t)),
            );
        }

        this._renderFilter();
    }

    updateStepInfo({ timestamp, eventType, author }) {
        const tsEl = this.el.querySelector("#replay-timestamp");
        tsEl.textContent = timestamp || "XX/XX/XXXX - XX:XX:XX";

        const eventEl = this.el.querySelector("#replay-event");
        eventEl.textContent = eventType || "--";

        const nameEl = this.el.querySelector("#replay-author-name");
        nameEl.textContent = author || "Desconocido";
    }

    updateProgress(position, total) {
        const bar = this.el.querySelector("#replay-progress-bar");
        bar.style.width = total > 0 ? `${(position / total) * 100}%` : "0%";

        this.el.querySelector("#replay-step-counter").textContent =
            `${position} / ${total}`;
    }

    show() {
        this.el.classList.add("visible");
    }

    hide() {
        this.el.classList.remove("visible");
        if (this.isPlaying) this.togglePlayPause();
    }

    _createFilterOption({ type, label, count }) {
        const option = document.createElement("label");
        option.className = "replay-filter-option";

        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.value = type;

        const name = document.createElement("span");
        name.className = "replay-filter-name";
        name.textContent = label;

        const counter = document.createElement("span");
        counter.className = "replay-filter-count";
        counter.textContent = count;

        option.append(checkbox, name, counter);
        return option;
    }

    _emitFilterChange() {
        this._renderFilter();
        this.onFilterChange?.(Array.from(this.selectedTypes));
    }

    _renderFilter() {
        this.el
            .querySelectorAll("#replay-filter-list input")
            .forEach((checkbox) => {
                checkbox.checked = this.selectedTypes.has(checkbox.value);
            });

        this.el.querySelector("#replay-filter-summary").textContent =
            `${this.selectedTypes.size}/${this.eventTypes.length}`;
    }
}
